<template>
  <div class="item-table-container" data-cy="item-table-testing">
    <b-table
      :items="itemsWithChilds"
      :fields="fields"
      responsive
      hover
      borderless
      sticky-header
      class="item-table"
    >
      <template v-slot:cell(details)="row">
        <b-icon
          :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'"
          @click="row.toggleDetails"
        >
        </b-icon>
      </template>
      <template v-slot:head(details)>
        <div></div>
      </template>
      <template v-slot:head(checkbox)>
        <input type="checkbox" :checked="selectAll" @change="handleSelectAll" />
      </template>
      <template v-slot:cell(checkbox)="{ item }">
        <input
          v-if="!item.parent"
          type="checkbox"
          :checked="selectedRows.includes(item)"
          @change="handleCheckboxChange(item)"
        />
      </template>
      <template v-slot:cell(imgThumbnail)="{ item }">
        <div class="text-center">
          <b-img
            v-if="item.imgThumbnail"
            class="text-center"
            fluid
            :src="item.imgThumbnail + '?width=50&height=50'"
          />
        </div>
      </template>
      <template v-slot:cell(browseModel)="{ item }" v-if="!isInputDisabled">
        <div class="d-flex"
          v-if="!item.parent"
        >
          <button
            class="btn-flat"
            @click="handleBrowse(item)"
            v-b-tooltip.hover
            title="Look Up"
          >
            <b-icon icon="search"></b-icon>
          </button>
          <button
            v-hideIf.cantWrite="{ module: Modules.ConfigurableMaterial }"
            size="sm"
            variant="secondary"
            class="btn-flat"
            @click="openConfigurationModal(item)"
            v-b-tooltip.hover
            title="Configure Material"
            v-if="(!item.pcnCatalogNumber || item.configurable) && displayConfigBtn()"
          >
            <b-icon icon="tools"></b-icon>
          </button>
          <b-icon title="Complete the configuration" icon="exclamation" v-if="item.configurable && !item.configured" variant="danger" class="h2"></b-icon>
          <b-icon title="Has special pricing. Changing the configuration will reset the price" icon="lock-fill" v-if="item.configurable && item.priceLocked" variant="warning" class="h5 mt-2"></b-icon>
          <configurable-material-selector-modal :ref="'selectorNew' + item.id" :item="item"></configurable-material-selector-modal>
        </div>
      </template>
      <template
        v-slot:cell(pcnCatalogNumber)="{ item }"
        v-if="!isInputDisabled"
      >
        <div style="display: flex;" v-if="!item.parent && !item.configurable || (item.parent)">
          <div>
            <pcn-search
              v-if="!item.parent"
              :catalogNumberProp="item.pcnCatalogNumber"
              @onSelect="(model) => onSelect(model, item)"
            />
            <label
              v-if="item.parent"
              class="text-center"
            >
            {{ item.pcnCatalogNumber }}
            </label>
          </div>
        </div>
        <div v-else>
          Configurable Material: <b>{{ item.pcnCatalogNumber }}</b>
        </div>
      </template>
      <template
        v-slot:cell(price)="data"
      >
        <p v-if="!data.item.parent && !data.item.configurable || (data.item.parent)">{{ data.value }}</p>
      </template>
      <template
        v-slot:cell(totalPrice)="data"
      >
        <p v-if="!data.item.parent && !data.item.configurable || (data.item.parent)">{{ data.value }}</p>
      </template>
      <template v-slot:cell(quantity)="{ item }">
        <input data-cy="item-quantity"
          v-if="!(!item.parent && item.configurable)"
          type="number"
          :value="item.quantity"
          class="form-control text-center quantity-input"
          @change="QtyChange($event, item)"
          min="1"
          :disabled="disableInput || (item.parent && item.totalPrice === 0)"
        />
      </template>
      <template v-slot:cell(fingoodTpiMfgMod)="{ item }">
        <div class="d-flex justify-center align-items-center">
          <div>{{ item.fingoodTpiMfgMod }}</div>
          <div v-if="item.modelDetails && Object.keys(item.modelDetails).length > 0">
            <button
              v-b-modal="item.pcnCatalogNumber + item.id"
              class="btn-flat"
              v-b-tooltip.hover
              title="View Model Details"
            >
              <b-icon icon="info-circle"></b-icon>
            </button>

            <b-modal
              :id="item.pcnCatalogNumber + item.id"
              title="Model Details"
              ok-only
              ok-title="Close"
            >
              <item-details :item="item" />
            </b-modal>
          </div>
        </div>
      </template>
      <template v-slot:cell(inventory)="{ item }" v-if="!isInputDisabled">
        <span class="mb-0 d-flex" style="gap: 5px">
          <modal-inventory :item="item" v-if="!item.configurable"></modal-inventory>
        </span>
      </template>
      <template v-slot:cell(customerMaterial)="{ item }">
        <span class="mb-0 d-flex" style="gap: 5px" v-if="item.pcnCatalogNumber && !item.parent">
          <b-form-input
            type="text"
            class="form-control"
            v-model="item.customerMaterial"
            maxlength="132"
          />
        </span>
      </template>
      <template v-slot:cell(actions)="{ item }">
        <div class="actions" data-cy="item-table-actions"
          v-if="!item.parent"
        >
          <button
            v-if="lineActions.includes(LineActionEnum.Delete)"
            class="btn-flat btn-red"
            @click="handleDelete(item)"
          >
            <b-icon icon="trash" />
          </button>
          <button
            v-if="lineActions.includes(LineActionEnum.Duplicate)"
            class="btn-flat"
            @click="handleDuplicate(item)"
          >
            <b-icon icon="files" />
          </button>
        </div>
      </template>
      <template #row-details="row">
        <b-card class="small">
          <item-details :item="row.item" />
          <b-button
            size="sm"
            class="mt-2 w-100"
            @click="row.toggleDetails"
            variant="outline"
            ><b-icon icon="chevron-double-up" variant="link"
          /></b-button>
        </b-card>
      </template>
    </b-table>
  </div>
</template>

<script lang="ts">
import { CartItem } from '@/store/cart/types'
import { Component, Prop } from 'vue-property-decorator'
import { BIcon, BImg } from 'bootstrap-vue'
import { ResponseArray } from '@/store/types'
import Multiselect from 'vue-multiselect'
import { debounce } from 'typescript-debounce-decorator'
import MxSite from '@/mixins/site'
import { LineAction } from '@/store/cart/constants'
import PcnSearch from '../search/pcn-search.vue'
import { Model } from '@/store/editor-model/types'
import { namespace } from 'vuex-class'
import ModalInventory from '../inventory/modal-inventory.vue'
import ItemDetails from './item-details.vue'
import { Api } from '@/api/Api'
import ConfigurableMaterialSelectorModal from '../modals/configurable-material-selector-modal.vue'

const nsMultiplier = namespace('multiplier')

@Component({
  components: {
    BIcon,
    BImg,
    Multiselect,
    PcnSearch,
    ModalInventory,
    ItemDetails,
    ConfigurableMaterialSelectorModal,
  },
})
export default class ItemTable extends MxSite {
  @Prop()
  public fields!: []

  @Prop()
  public items!: CartItem[]

  @Prop()
  public selectedRows!: CartItem[]

  @Prop()
  public selectedItems!: CartItem[]

  @Prop()
  public selectAll!: boolean

  @Prop()
  public disableInput!: boolean

  @Prop()
  public searchResult!: ResponseArray<CartItem>

  @Prop()
  public handleDelete!: (item: CartItem) => void

  @Prop()
  public handleDuplicate!: (item: CartItem) => void

  @Prop()
  public handleCheckboxChange!: (item: CartItem) => void

  @Prop()
  public handleSelectAll!: () => void

  @Prop()
  public handleQtyChange!: (event: any, item: CartItem) => void

  @Prop()
  public handleSearch!: (event: any) => void

  @Prop()
  public handleBrowse!: (item: CartItem) => void

  @Prop()
  public handleSelect!: (item: CartItem) => void

  @Prop()
  public handleLocationChange!: (event: any, item: CartItem) => void

  @nsMultiplier.Getter('getMultiplierPrice')
  private getMultiplierPrice: any

  @Prop({ default: () => [LineAction.Delete, LineAction.Duplicate] })
  public lineActions!: LineAction[]

  private api = new Api()

  public LineActionEnum = LineAction
  public showConfigurationModal: string = 'null'

  public formatPrice(cost: number): string {
    return `$${cost.toFixed(2)}`
  }

  public selectedModelItem: any = null

  @debounce(1000)
  public async asyncFind(query: string) {
    this.handleSearch(query)
  }

  public QtyChange(event: any, item: CartItem) {
    this.handleQtyChange(event, item)
    if (item.parent && item.parent.subItems[0].TotalPrice === 0 && item.parent.subItems[1].MaterialNumber === item.pcnCatalogNumber) {
      const previousItem = this.itemsWithChilds.filter((i) => i.parent === item.parent).find((previous, index, array) => {
        const arrayItem = index < array.length ? array[index + 1] : null
        return arrayItem?.pcnCatalogNumber === item.pcnCatalogNumber && previous
      })
      if (previousItem) {
        previousItem.quantity = item.quantity
        this.handleQtyChange(event, previousItem)
      }
    }
  }

  public get itemsWithChilds() {
    const items: CartItem[] = []
    for (const itemIndex in this.items) {
      const item = this.items[itemIndex]
      items.push(item)
      if (item.subItems) {
        for (const subItemIndex in item.subItems) {
          const subItem = item.subItems[subItemIndex]
          const conditionPrices = subItem.Conditions?.filter((s: any) => s.VariantCondition === 'MISC_SURCHARGE' || s.VariantCondition === 'NEMA4').reduce((partialSum: any, a: any) => partialSum + a.ConditionAmount, 0) ?? 0
          let multiplier = subItem.SpecialMultiplier ?? subItem.Multiplier
          if (multiplier <= 0) {
            multiplier = 100 + multiplier
          }
          let total = subItem.TotalPrice / subItem.Quantity
          total += (conditionPrices * multiplier / 100)
          total = total * subItem.Quantity
          const cartItem: CartItem = {
            parent: item,
            configurable: true,
            pcnCatalogNumber: subItem.MaterialNumber,
            SapModelDescription: subItem.Description,
            fingoodTpiMfgMod: '',
            description: subItem.Description,
            quantity: subItem.Quantity * item.quantity,
            price: total / subItem.Quantity,
            totalPrice: total * item.quantity,
            imgThumbnail: '',
            shippingStrategyId: '0',
            originalPrice: subItem.Price,
            productHierarchy: '',
            inventory: '',
            leadTime: 0,
          }
          items.push(cartItem)
        }
      }
    }
    return items
  }

  public openConfigurationModal(item) {
    const refs: any = this.$refs
    const myModal: any = refs['selectorNew' + item.id]
    myModal.open()
  }

  public displayConfigBtn() {
    console.log('displayConfigBtn', this.role, this.$route.name)
    return this._isCustomerAdminSite(this.role || '') || ((this.$route.name?.indexOf('inquiry') ?? -1) >= 0)
  }

  public onSelect(model: Model, currentItem: CartItem) {
    const price = this.getMultiplierPrice(
      model.PcnCatalogNumber,
      model.ProductHierarchy,
      model.TotalList,
    )
    const item: CartItem = {
      ...currentItem,
      pcnCatalogNumber: model.PcnCatalogNumber || '',
      SapModelDescription: model.SapModelDescription || '',
      fingoodTpiMfgMod: model.FingoodTpiMfgMod || '',
      description:
        model?.Descriptions[0]?.toString() || model.SapModelDescription || '',
      quantity: model.PcnCatalogNumber ? 1 : 0,
      price: price || 0,
      totalPrice: price || 0,
      imgThumbnail: model.Image?.ImagePath || '',
      shippingStrategyId: model.ShippingStrategyId || 0,
      originalPrice: model.TotalList || 0,
      productHierarchy: model.ProductHierarchy || '',
      inventory: model.Available || '',
      modelDetails: model.Details,
      leadTime: model.LeadTime,
      configurable: model.Configurable,
    }
    this.handleSelect(item)
  }

  public customLabel({
    pcnCatalogNumber,
    fingoodTpiMfgMod,
  }: {
    pcnCatalogNumber: string
    fingoodTpiMfgMod: string
  }) {
    return `${pcnCatalogNumber} - ${fingoodTpiMfgMod}`
  }

  public get isInputDisabled() {
    return (
      this.disableInput ||
      (!this._isCustomerSite(this.site.Name) &&
        !this._isCustomerAdminSite(this.role || '') &&
        !this._isSalesRepSite(this.site.Name))
    )
  }
}
</script>

<style scoped>
.item-icon {
  font-size: 2rem;
}
.item-img {
  width: 60px;
}

.quantity-input {
  max-width: 80px;
  float: right;
}

.image-soon {
  height: 50px;
  width: 50px;
}

.actions {
  display: flex;
}

.pcn-model-search {
  display: flex;
}

.item-table {
  display: contents;
}
</style>
